/**
 * QuickBooks Online (QBO) integration contract.
 *
 * Catalyst pushes *issued* invoices into a single production QBO company (no
 * sandbox — see docs/conventions.md "QBO safety"). The connection is established
 * once via Intuit's OAuth2 authorization-code flow; the resulting tokens are stored
 * encrypted at rest and never leave the backend. This contract intentionally
 * exposes **no** token material — only connection status metadata.
 *
 * Every push is gated by `QBO_DRY_RUN` (default `true`): in dry-run mode the Intuit
 * invoice payload is built and recorded but never posted, so the mapping can be
 * verified safely before real money documents are created.
 */
// ---------------------------------------------------------------------------
// Connection
// ---------------------------------------------------------------------------
export const QBO_CONNECTION_STATUSES = ['disconnected', 'connected', 'expired'];
/** Only the production Intuit environment is supported in v1. */
export const QBO_ENVIRONMENTS = ['production'];
/** OAuth scope required to read/write accounting data (invoices, customers). */
export const QBO_ACCOUNTING_SCOPE = 'com.intuit.quickbooks.accounting';
// ---------------------------------------------------------------------------
// Invoice sync
// ---------------------------------------------------------------------------
export const QBO_SYNC_STATUSES = ['pending', 'dry_run', 'synced', 'failed'];
// ---------------------------------------------------------------------------
// Sync log (detailed, one entry per invoice per single/batch run)
// ---------------------------------------------------------------------------
/** How an invoice was pushed to QBO: as a lone invoice, or as part of a batch. */
export const QBO_SYNC_MODES = ['single', 'batch'];
export const QBO_SYNC_MODE_LABELS = {
    single: 'Single',
    batch: 'Batch',
};
// ---------------------------------------------------------------------------
// Mapping preview (what SKU + class each invoice line will send to QBO)
// ---------------------------------------------------------------------------
/** Where a line's resolved SKU or class came from. */
export const QBO_MAPPING_SOURCES = [
    'service_category',
    'ticket_type',
    'product',
    'qbo_overage',
    'qbo_default_item',
    'none',
];
/** Human labels for each mapping source (shown in the SKU/class tooltip). */
export const QBO_MAPPING_SOURCE_LABELS = {
    service_category: 'Service category',
    ticket_type: 'Ticket type',
    product: 'Product',
    qbo_overage: 'QuickBooks settings (overage)',
    qbo_default_item: 'QuickBooks default item',
    none: 'Not set',
};
// ---------------------------------------------------------------------------
// Customer finance (QBO balances + invoice payment status for the Finance tab)
// ---------------------------------------------------------------------------
/** Payment state of a QBO invoice (derived from its balance + due date). */
export const QBO_INVOICE_PAYMENT_STATUSES = ['paid', 'partial', 'open', 'overdue'];
export const QBO_INVOICE_PAYMENT_STATUS_LABELS = {
    paid: 'Paid',
    partial: 'Partially paid',
    open: 'Open',
    overdue: 'Overdue',
};
// ---------------------------------------------------------------------------
// Scheduled payment sync (mirror QBO paid/overdue state onto PSA invoices)
// ---------------------------------------------------------------------------
/**
 * Payment states the sync can stamp on a PSA invoice: the real QBO-derived states
 * plus `not_found` (no QuickBooks invoice matched by id or number).
 */
export const INVOICE_PAYMENT_SYNC_STATUSES = [
    ...QBO_INVOICE_PAYMENT_STATUSES,
    'not_found',
];
export const INVOICE_PAYMENT_SYNC_STATUS_LABELS = {
    ...QBO_INVOICE_PAYMENT_STATUS_LABELS,
    not_found: 'Not found in QBO',
};
/** Allowed interval bounds for the scheduled payment sync (minutes). */
export const QBO_PAYMENT_SYNC_MIN_INTERVAL_MINUTES = 60;
export const QBO_PAYMENT_SYNC_MAX_INTERVAL_MINUTES = 10080;
/** Default cadence: daily. */
export const QBO_PAYMENT_SYNC_DEFAULT_INTERVAL_MINUTES = 1440;
export const QBO_PAYMENT_SYNC_OUTCOMES = ['ok', 'partial', 'failed'];
//# sourceMappingURL=qbo.js.map